import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth, USER_ROLES } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import NotificationManager from './components/NotificationManager';
import './App.css';

// Lazy load components for better performance and code splitting
const Login = lazy(() => import('./pages/Auth/Login'));
const Signup = lazy(() => import('./pages/Auth/Signup'));
const PendingApproval = lazy(() => import('./pages/Auth/PendingApproval'));
const ChangePassword = lazy(() => import('./pages/Auth/ChangePassword'));
const VerifyEmail = lazy(() => import('./pages/Auth/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const VerifyAdminGmail = lazy(() => import('./pages/Admin/VerifyAdminGmail'));
const AdminProfile = lazy(() => import('./pages/Admin/AdminProfile'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard/AdminDashboard'));
const AddUsers = lazy(() => import('./pages/AgentTeam/AddUsers'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const ApplicationHistory = lazy(() => import('./pages/Applications/History'));
const ApplyEnquiry = lazy(() => import('./pages/Applications/ApplyEnquiry'));
const StudentProfile = lazy(() => import('./pages/Applications/StudentProfile'));
const PartnerUniversities = lazy(() => import('./pages/Universities/PartnerList'));
const CourseSearch = lazy(() => import('./pages/Universities/CourseSearch'));
const Notice = lazy(() => import('./pages/Finance/Notice'));
const TrainingHub = lazy(() => import('./pages/Finance/Training'));
const UniversityDeadlines = lazy(() => import('./pages/Deadlines/UniversityDeadlines'));
const CreativeGeneration = lazy(() => import('./pages/Agent/CreativeGeneration/CreativeGeneration'));
const FinanceAccount = lazy(() => import('./pages/Finance/Accounts/MainDashboard'));
const Commissions = lazy(() => import('./pages/Finance/Commissions'));
const PromotionalCommission = lazy(() => import('./pages/Finance/Promotions'));
const CompanyDetails = lazy(() => import('./pages/Agent/companydetails'));
const Inbox = lazy(() => import('./pages/Admin/AdminDashboard/AdminChat/Inbox'));
const SupportChatPage = lazy(() => import('./pages/Agent/SupportChatPage'));
const AdminChatInbox = lazy(() => import('./pages/Admin/AdminDashboard/AdminChat/AdminChatInbox'));
const ApplicationFlow = lazy(() => import('./components/ApplicationFlow/ApplicationFlow'));
const UserDetails = lazy(() => import('./pages/Profile/UserDetails'));
const BankDetails = lazy(() => import('./pages/Profile/BankDetails'));
const Documents = lazy(() => import('./pages/Profile/Documents'));
const Transactions = lazy(() => import('./pages/Profile/Transactions'));
const MOUVerification = lazy(() => import('./pages/Profile/MOUVerification'));
const Accounts = lazy(() => import('./pages/Accounts/Accounts'));
const Referrals = lazy(() => import('./pages/Referrals/Referrals'));
const AllNotifications = lazy(() => import('./pages/Notifications/AllNotifications'));
const AdminAllNotifications = lazy(() => import('./pages/Notifications/AdminAllNotifications'));
const EmailCommunication = lazy(() => import('./pages/Admin/AdminDashboard/EmailCommunication/EmailCommunication'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Vouchers = lazy(() => import('./pages/Vouchers'));
const Taskmain = lazy(() => import('./pages/TaskManager/Taskmain'));
const Assignedtask = lazy(() => import('./pages/TaskManager/Assignedtask'));
const Assignedtome = lazy(() => import('./pages/TaskManager/Assignedtome'));
const Taskcard = lazy(() => import('./pages/TaskManager/Taskcard'));
const InterviewDashboard = lazy(() => import('./pages/Interview/InterviewDashboard'));
const InterviewStudents = lazy(() => import('./pages/Interview/InterviewStudents'));
const InterviewSchedule = lazy(() => import('./pages/Interview/InterviewSchedule'));
const InterviewApplications = lazy(() => import('./pages/Interview/InterviewApplications'));
const InterviewEvaluation = lazy(() => import('./pages/Interview/InterviewEvaluation'));
const InterviewerProfile = lazy(() => import('./pages/Interview/InterviewerProfile'));
const AgentInterviewSchedule = lazy(() => import('./pages/Applications/AgentInterviewSchedule'));
const RelationshipManagerDashboard = lazy(() => import('./pages/RelationshipManager/RelationshipManagerDashboard'));
const RelationshipManagerProfile = lazy(() => import('./pages/RelationshipManager/RelationshipManagerProfile'));
const RelationshipManagerAgentDetail = lazy(() => import('./pages/RelationshipManager/Agents/AgentDetail'));
const RelationshipManagerAgentsList = lazy(() => import('./pages/RelationshipManager/Agents/AgentsList'));
const RelationshipManagerApplications = lazy(() => import('./pages/RelationshipManager/Applications/ApplicationsList'));
const RelationshipManagerWeeklyReports = lazy(() => import('./pages/RelationshipManager/WeeklyReports/WeeklyReports'));

// Loading component
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    fontSize: '16px',
    color: '#666'
  }}>
    Loading...
  </div>
);

const ADMIN_ROLES = [USER_ROLES.ADMIN, USER_ROLES.SEMI_ADMIN];
const AGENT_ROLES = [USER_ROLES.AGENT, 'Semi Agent'];
const TEAM_MANAGEMENT_ROLES = [...ADMIN_ROLES, ...AGENT_ROLES];
const FINANCE_ROLES = [USER_ROLES.ADMIN, USER_ROLES.AGENT, 'Semi Agent'];
const INTERVIEWER_ROLES = [USER_ROLES.INTERVIEWER];
const RELATIONSHIP_MANAGER_ROLES = [USER_ROLES.RELATIONSHIP_MANAGER];

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], excludedRoles = [], requireRole = null }) => {
  const { isAuthenticated, user, isLoading, getUserDashboardRoute } = useAuth();
  const redirectTo = getUserDashboardRoute() || '/dashboard';

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is approved (admin, semi-admin, interviewer, and relationship manager users are always approved)
  if (!user.isApproved && user.role !== 'Admin' && user.role !== 'Semi Admin' && user.role !== 'Interviewer' && user.role !== 'Relationship Manager') {
    return <Navigate to="/pending-approval" replace />;
  }

  if (user && excludedRoles.length > 0 && excludedRoles.includes(user.role)) {
    return <Navigate to={redirectTo} replace />;
  }

  const requiredRoles = new Set(allowedRoles);
  const rolesFromRequireProp = Array.isArray(requireRole)
    ? requireRole
    : requireRole
      ? [requireRole]
      : [];
  rolesFromRequireProp.forEach(role => requiredRoles.add(role));

  if (requiredRoles.size > 0 && (!user || !requiredRoles.has(user.role))) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

// Main App Component
const ProfileRoute = () => {
  const { isInterviewer, isRelationshipManager } = useAuth();
  if (isInterviewer()) {
    return <Navigate to="/interview/profile" replace />;
  }
  if (isRelationshipManager()) {
    return <Navigate to="/relationship-manager/profile" replace />;
  }
  return <UserDetails />;
};

// Interview Schedule Route - renders different component based on user role
const InterviewScheduleRoute = () => {
  const { isAgent, isSemiAgent, isInterviewer } = useAuth();
  
  if (isAgent() || isSemiAgent()) {
    return <AgentInterviewSchedule />;
  } else if (isInterviewer()) {
    return <InterviewSchedule />;
  }
  
  // Fallback (shouldn't reach here due to ProtectedRoute)
  return <Navigate to="/dashboard" replace />;
};

const AppContent = () => {
  const { isAuthenticated, user, getUserDashboardRoute, isInterviewer } = useAuth();
  const defaultDashboardRoute = getUserDashboardRoute();
  const isAdminUser = user && (user.role === 'Admin' || user.role === 'Semi Admin');

  return (
    <>
    <Suspense fallback={<LoadingFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/landing" element={<LandingPage />} />
      <Route 
        path="/vouchers" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <Vouchers />
          </ProtectedRoute>
        } 
      />
      <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to={defaultDashboardRoute} replace />} />
      <Route path="/signup" element={!isAuthenticated ? <Signup /> : <Navigate to={defaultDashboardRoute} replace />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route 
        path="/admin/verify-gmail" 
        element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <VerifyAdminGmail />
          </ProtectedRoute>
        } 
      />
      <Route path="/pending-approval" element={<PendingApproval />} />
      <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to={defaultDashboardRoute} replace />} />
      <Route path="/reset-password" element={!isAuthenticated ? <ResetPassword /> : <Navigate to={defaultDashboardRoute} replace />} />
      
      {/* Protected Routes - Admin Dashboard with nested routes */}
      <Route 
        path="/admin/dashboard/*" 
        element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Change Password Route */}
      <Route 
        path="/change-password" 
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        } 
      />
      
      {/* Dashboard Route */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Agent Dashboard Routes */}
      <Route 
        path="/application-history" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <ApplicationHistory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/application-flow" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <ApplicationFlow />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/student/:camsId" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <StudentProfile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/apply-enquiry" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <ApplyEnquiry />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/partner-universities" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <PartnerUniversities />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/search-courses" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <CourseSearch />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/university-deadlines" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <UniversityDeadlines />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/creative-generation" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <CreativeGeneration />
          </ProtectedRoute>
        } 
      />
      
      {/* Finance Routes - Restricted for Semi Admin users */}
      <Route 
        path="/finance/notice" 
        element={
          <ProtectedRoute allowedRoles={FINANCE_ROLES}>
            <Notice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/training" 
        element={
          <ProtectedRoute allowedRoles={FINANCE_ROLES}>
            <TrainingHub />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/account" 
        element={
          <ProtectedRoute allowedRoles={FINANCE_ROLES}>
            <FinanceAccount />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/commissions" 
        element={
          <ProtectedRoute allowedRoles={FINANCE_ROLES}>
            <Commissions />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finance/promotional-commission" 
        element={
          <ProtectedRoute allowedRoles={FINANCE_ROLES}>
            <PromotionalCommission />
          </ProtectedRoute>
        } 
      />
      
      {/* Agent specific routes */}
      <Route 
        path="/agent/company-details" 
        element={
          <ProtectedRoute allowedRoles={AGENT_ROLES}>
            <CompanyDetails />
          </ProtectedRoute>
        } 
      />
      
      {/* Add Users / Team Management Route */}
      <Route 
        path="/admin/add-users" 
        element={
          <ProtectedRoute allowedRoles={TEAM_MANAGEMENT_ROLES}>
            <AddUsers />
          </ProtectedRoute>
        } 
      />
      
      {/* Inbox Route */}
      <Route 
        path="/inbox" 
        element={
          <ProtectedRoute>
            <Inbox />
          </ProtectedRoute>
        } 
      />
      
      {/* Accounts route */}
      <Route 
        path="/accounts" 
        element={
          <ProtectedRoute>
            <Accounts />
          </ProtectedRoute>
        } 
      />
      
      {/* Referrals route */}
      <Route 
        path="/referrals" 
        element={
          <ProtectedRoute>
            <Referrals />
          </ProtectedRoute>
        } 
      />
      
      {/* Notifications route */}
      <Route 
        path="/notifications" 
        element={
          <ProtectedRoute>
            <AllNotifications />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin All Notifications route (opens in new window) */}
      <Route 
        path="/admin/notifications/all" 
        element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <AdminAllNotifications />
          </ProtectedRoute>
        } 
      />
      
      {/* Profile routes - Available for all roles including Admin and Semi Admin */}
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfileRoute />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile/company-details" 
        element={
          <ProtectedRoute excludedRoles={['Admin', 'Semi Admin']}>
            <CompanyDetails />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile/bank-details" 
        element={
          <ProtectedRoute excludedRoles={['Admin', 'Semi Admin']}>
            <BankDetails />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile/documents" 
        element={
          <ProtectedRoute excludedRoles={['Admin', 'Semi Admin']}>
            <Documents />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile/transactions" 
        element={
          <ProtectedRoute excludedRoles={['Admin', 'Semi Admin']}>
            <Transactions />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile/mou-verification" 
        element={
          <ProtectedRoute excludedRoles={['Admin', 'Semi Admin']}>
            <MOUVerification />
          </ProtectedRoute>
        } 
      />
      
      {/* Task Manager Routes */}
      <Route 
        path="/tasks/main" 
        element={
          <ProtectedRoute>
            <Taskmain />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks/assigned" 
        element={
          <ProtectedRoute>
            <Assignedtask />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks/assigned-to-me" 
        element={
          <ProtectedRoute>
            <Assignedtome />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks/card/:taskId?" 
        element={
          <ProtectedRoute>
            <Taskcard />
          </ProtectedRoute>
        } 
      />
      
      {/* Interview Routes */}
      <Route 
        path="/interview/dashboard" 
        element={
          <ProtectedRoute allowedRoles={INTERVIEWER_ROLES}>
            <InterviewDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/interview/profile" 
        element={
          <ProtectedRoute allowedRoles={INTERVIEWER_ROLES}>
            <InterviewerProfile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/interview/students" 
        element={
          <ProtectedRoute allowedRoles={INTERVIEWER_ROLES}>
            <InterviewStudents />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/interview/schedule" 
        element={
          <ProtectedRoute allowedRoles={[...INTERVIEWER_ROLES, ...AGENT_ROLES]}>
            <InterviewScheduleRoute />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/interview/applications" 
        element={
          <ProtectedRoute allowedRoles={INTERVIEWER_ROLES}>
            <InterviewApplications />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/interview/evaluation" 
        element={
          <ProtectedRoute allowedRoles={INTERVIEWER_ROLES}>
            <InterviewEvaluation />
          </ProtectedRoute>
        } 
      />
      
      {/* Relationship Manager Routes */}
      <Route 
        path="/relationship-manager/dashboard" 
        element={
          <ProtectedRoute allowedRoles={RELATIONSHIP_MANAGER_ROLES}>
            <RelationshipManagerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/relationship-manager/profile" 
        element={
          <ProtectedRoute allowedRoles={RELATIONSHIP_MANAGER_ROLES}>
            <RelationshipManagerProfile />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/relationship-manager/applications" 
        element={
          <ProtectedRoute allowedRoles={RELATIONSHIP_MANAGER_ROLES}>
            <RelationshipManagerApplications />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/relationship-manager/agents" 
        element={
          <ProtectedRoute allowedRoles={RELATIONSHIP_MANAGER_ROLES}>
            <RelationshipManagerAgentsList />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/relationship-manager/agents/:agentId" 
        element={
          <ProtectedRoute allowedRoles={RELATIONSHIP_MANAGER_ROLES}>
            <RelationshipManagerAgentDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/relationship-manager/weekly-reports" 
        element={
          <ProtectedRoute allowedRoles={RELATIONSHIP_MANAGER_ROLES}>
            <RelationshipManagerWeeklyReports />
          </ProtectedRoute>
        } 
      />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to={isAuthenticated ? defaultDashboardRoute : "/login"} replace />} />
      <Route path="*" element={<Navigate to={isAuthenticated ? defaultDashboardRoute : "/login"} replace />} />
    </Routes>
    </Suspense>
    {/* Render floating chat inbox for admin users on all pages (including /inbox) - Temporarily hidden */}
    {/* {isAdminUser && (window.location.pathname.startsWith('/admin') || window.location.pathname === '/inbox') && (
      <Suspense fallback={null}>
        <AdminChatInbox />
      </Suspense>
    )} */}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <ChatProvider>
      <Router>
          <AppContent />
          <NotificationManager />
        </Router>
      </ChatProvider>
    </AuthProvider>
  );
}

export default App;
